import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ChatModal from './ChatModal';
import { getAuthHeaders } from '../lib/authHeaders';
import {
  Users, 
  Circle, 
  Gamepad2, 
  MessageCircle,
  RefreshCw,
  ExternalLink,
  Swords,
  ChevronUp,
  ChevronDown,
  MessageSquare,
  LogIn,
  UserPlus,
  Mail,
  Search,
  Star,
  X
} from 'lucide-react';

import { API } from '../config/api';

// Helper to get auth headers
// getAuthHeaders imported from shared utility

// Status color mapping
const statusColors = {
  online: 'bg-green-500',
  idle: 'bg-yellow-500',
  dnd: 'bg-red-500',
  offline: 'bg-gray-500',
  unknown: 'bg-blue-500'
};

// Discord Logo SVG Component
const DiscordLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

// Discord DM link helper
const getDiscordDMLink = (userId) => {
  return `https://discord.com/users/${userId}`;
};

const DiscordBottomBar = ({ user }) => {
  const [discordStatus, setDiscordStatus] = useState({
    connected: false,
    in_server: false,
    discord_username: null,
    discord_avatar: null,
    server_invite: 'https://discord.gg/b9zCaA7BkF'
  });
  const [allMembers, setAllMembers] = useState([]);
  const [onlineCount, setOnlineCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatRecipient, setChatRecipient] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [friends, setFriends] = useState([]);
  const [showInbox, setShowInbox] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(false);

  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail
    }
  }, [user]);

  // Fetch Discord status
  const fetchDiscordStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discord/status`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      setDiscordStatus(response.data);
    } catch (error) {
      console.error('Error fetching Discord status:', error);
    }
  }, []);

  // Fetch online members
  const fetchOnlineMembers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/discord/online-members`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      setAllMembers(response.data.members || []);
      // Count only truly online users
      const online = (response.data.members || []).filter(m => m.status === 'online' || m.status === 'unknown');
      setOnlineCount(online.length);
    } catch (error) {
      console.error('Error fetching online members:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch friends list
  const fetchFriends = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/discord/friends`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      setFriends(response.data.friends || []);
    } catch (error) {
      // Silently fail
    }
  }, [user]);

  // Fetch conversations/inbox
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    setLoadingInbox(true);
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      // Ensure we have an array of conversations
      const convData = response.data?.conversations;
      if (Array.isArray(convData)) {
        setConversations(convData);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoadingInbox(false);
    }
  }, [user]);

  // Open inbox and fetch conversations
  const openInbox = () => {
    if (!user) {
      toast.error('Please login to view messages');
      return;
    }
    setShowInbox(true);
    fetchConversations();
  };

  // Open chat from inbox
  const openChatFromInbox = (conv) => {
    setShowInbox(false);
    setChatRecipient({
      user_id: conv.user_id,
      name: conv.name || conv.discord_username || 'Unknown',
      avatar: conv.avatar,
      discord_username: conv.discord_username
    });
    setChatOpen(true);
  };

  // Update activity
  const updateActivity = useCallback(async () => {
    try {
      await axios.post(`${API}/discord/update-activity`, {}, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchDiscordStatus();
    fetchOnlineMembers();
    fetchUnreadCount();
    fetchFriends();
    if (user) updateActivity();

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchOnlineMembers();
      fetchUnreadCount();
      if (user) updateActivity();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchDiscordStatus, fetchOnlineMembers, fetchUnreadCount, fetchFriends, updateActivity, user]);

  // Filter members: only online, no bots, friends first, apply search
  const filteredMembers = useMemo(() => {
    // Common bot names to filter out (frontend safety net)
    const BOT_NAMES = ['MEE6', 'Dyno', 'Carl-bot', 'Dank Memer', 'Groovy', 'Rythm', 'FredBoat', 
                       'Mudae', 'Pokétwo', 'Tatsu', 'YAGPDB.xyz', 'ProBot', 'Arcane', 'ServerStats',
                       'Ticket Tool', 'TicketTool', 'Captcha.bot', 'Hydra', 'Jockie Music'];
    
    // Only show online users (not idle, dnd, or offline) and filter out bots
    let online = allMembers.filter(m => {
      // Check online status
      if (m.status !== 'online' && m.status !== 'unknown') return false;
      
      // Filter out bots
      if (m.is_bot === true) return false;
      
      const username = m.username || '';
      const displayName = m.display_name || '';
      
      // Check against known bot names
      if (BOT_NAMES.some(bot => 
        username.toLowerCase() === bot.toLowerCase() || 
        displayName.toLowerCase() === bot.toLowerCase()
      )) return false;
      
      // Check for bot patterns in name
      if (username.endsWith('Bot') || username.endsWith('bot') || 
          displayName.endsWith('Bot') || displayName.endsWith('bot') ||
          username.includes('[BOT]') || displayName.includes('[BOT]')) return false;
      
      return true;
    });
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      online = online.filter(m => 
        (m.display_name || '').toLowerCase().includes(query) ||
        (m.username || '').toLowerCase().includes(query)
      );
    }
    
    // Sort: Friends first, then by name
    const friendIds = new Set(friends.map(f => f.discord_id));
    
    return online.sort((a, b) => {
      const aIsFriend = friendIds.has(a.id);
      const bIsFriend = friendIds.has(b.id);
      
      if (aIsFriend && !bIsFriend) return -1;
      if (!aIsFriend && bIsFriend) return 1;
      
      return (a.display_name || a.username || '').localeCompare(b.display_name || b.username || '');
    });
  }, [allMembers, searchQuery, friends]);

  const friendIds = useMemo(() => new Set(friends.map(f => f.discord_id)), [friends]);

  // Open chat with a member
  const openChat = (member) => {
    if (!user) {
      toast.error('Please login to send messages');
      return;
    }
    setChatRecipient({
      user_id: member.user_id || member.id,
      name: member.display_name || member.username,
      avatar: member.avatar,
      discord_username: member.username
    });
    setChatOpen(true);
  };

  const handleConnectDiscord = async () => {
    setIsConnecting(true);
    try {
      const response = await axios.get(`${API}/auth/discord/login`, {
        withCredentials: true,
        headers: await getAuthHeaders()
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect to Discord');
      setIsConnecting(false);
    }
  };

  const joinDiscordServer = () => {
    window.open(discordStatus.server_invite, '_blank');
  };

  // Minimized state - just show a small tab
  if (!isExpanded) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50">
        <button
          onClick={() => setIsExpanded(true)}
          className="mx-auto block bg-[#5865F2] hover:bg-[#4752C4] text-white px-6 py-2 rounded-t-lg shadow-lg transition-all flex items-center gap-2"
        >
          <DiscordLogo className="w-5 h-5" />
          <span className="font-medium">Discord</span>
          <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">{onlineCount} Online</span>
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-[#2B2D31] via-[#1E1F22] to-[#2B2D31] dark:from-[#2B2D31] dark:via-[#1E1F22] dark:to-[#2B2D31] border-t border-[#3F4147] dark:border-[#3F4147] shadow-2xl">
      {/* Collapse Button */}
      <button
        onClick={() => setIsExpanded(false)}
        className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#5865F2] hover:bg-[#4752C4] text-white px-4 py-1 rounded-t-lg text-sm flex items-center gap-1"
      >
        <ChevronDown className="w-4 h-4" />
        Minimize
      </button>

      <div className="max-w-full mx-auto px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Discord Logo & Status with Search */}
          <div className="flex items-center gap-3 pr-4 border-r border-gray-700 dark:border-gray-700">
            <div className="bg-[#5865F2] p-2 rounded-lg">
              <DiscordLogo className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white dark:text-white font-semibold text-sm">Squidemy Discord</p>
              {/* Only show online count and search to logged-in users */}
              {user ? (
                <div className="flex items-center gap-2">
                  <Circle className="w-2 h-2 text-green-500 fill-green-500" />
                  <span className="text-green-400 dark:text-green-400 text-xs">{onlineCount} Online</span>
                  {/* Inline Search Toggle */}
                  <button
                    onClick={() => setShowSearch(!showSearch)}
                    className={`p-1 rounded transition-all ${
                      showSearch 
                        ? 'bg-cyan-600/30 text-cyan-400' 
                        : 'text-gray-400 hover:text-white hover:bg-slate-700/50'
                    }`}
                    title="Search members"
                  >
                    <Search className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <p className="text-gray-400 text-xs">Join our community</p>
              )}
              {/* Search Input - appears below when toggled - only for logged-in users */}
              {user && showSearch && (
                <div className="relative mt-1">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-6 text-xs pl-2 pr-6 w-36 bg-slate-800/80 border-slate-600 text-white placeholder:text-gray-400"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Connection Status / Login Prompt */}
          {!user ? (
            // Not logged in - show login prompt
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/50 dark:bg-slate-800/50 rounded-lg border border-slate-700 dark:border-slate-700">
              <LogIn className="w-5 h-5 text-yellow-400" />
              <span className="text-gray-300 dark:text-gray-300 text-sm">Sign in to connect with players</span>
              <Button
                size="sm"
                onClick={() => window.location.href = '/login'}
                className="bg-[#5865F2] hover:bg-[#4752C4] text-white"
              >
                Login
              </Button>
            </div>
          ) : !discordStatus.connected ? (
            // Logged in but Discord not connected
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/50 dark:bg-slate-800/50 rounded-lg border border-yellow-700/50">
              <UserPlus className="w-5 h-5 text-yellow-400" />
              <span className="text-gray-300 dark:text-gray-300 text-sm">Connect Discord to play challenges</span>
              <Button
                size="sm"
                onClick={handleConnectDiscord}
                disabled={isConnecting}
                className="bg-[#5865F2] hover:bg-[#4752C4] text-white"
              >
                {isConnecting ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Connect Discord'}
              </Button>
            </div>
          ) : !discordStatus.in_server ? (
            // Connected but not in server
            <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/50 rounded-lg border border-yellow-700/50">
              <Users className="w-5 h-5 text-yellow-400" />
              <span className="text-gray-300 text-sm">Join our server to unlock challenges!</span>
              <Button
                size="sm"
                onClick={joinDiscordServer}
                className="bg-[#5865F2] hover:bg-[#4752C4] text-white"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Join Server
              </Button>
            </div>
          ) : (
            // Fully connected - show user info (clean, no search here)
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-900/30 dark:bg-green-900/30 rounded-lg border border-green-700/50">
              {discordStatus.discord_avatar ? (
                <img src={discordStatus.discord_avatar} alt="" className="w-6 h-6 rounded-full" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-[#5865F2] flex items-center justify-center">
                  <DiscordLogo className="w-3 h-3 text-white" />
                </div>
              )}
              <span className="text-green-400 dark:text-green-400 text-sm font-medium">{discordStatus.discord_username}</span>
              <span className="text-green-500 dark:text-green-500 text-xs">✓ Connected</span>
            </div>
          )}

          {/* Online Players - Horizontal Scroll - Only show to logged-in users */}
          {user ? (
            <div className="flex-1 overflow-hidden">
              {/* Scrollable container showing ALL members - centered and clean */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-1 scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent hover:scrollbar-thumb-slate-500">
                {isLoading ? (
                  <RefreshCw className="w-5 h-5 text-gray-400 animate-spin mx-4" />
                ) : filteredMembers.length > 0 ? (
                  filteredMembers.map((member, index) => {
                    const isFriend = friendIds.has(member.id);
                    return (
                      <div
                        key={member.id || index}
                        className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-700/50 dark:hover:bg-slate-700/50 transition-colors group cursor-pointer ${
                          isFriend ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-slate-800/50 dark:bg-slate-800/50'
                        }`}
                        title={`${member.display_name || member.username}${member.game ? ` - Playing: ${member.game}` : ''}${isFriend ? ' ⭐ Friend' : ''}`}
                      >
                        {/* Friend Star */}
                        {isFriend && (
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                        )}
                        
                        {/* Avatar */}
                        <div className="relative">
                          {member.avatar ? (
                            <img src={member.avatar} alt="" className="w-7 h-7 rounded-full" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-slate-600 flex items-center justify-center text-white dark:text-white text-xs font-bold">
                              {(member.display_name || member.username || '?')[0].toUpperCase()}
                            </div>
                          )}
                          {/* Green online indicator only */}
                          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#1E1F22] dark:border-[#1E1F22] bg-green-500" />
                        </div>
                        
                        {/* Name - Show up to 20 characters */}
                        <span className="text-white dark:text-white text-xs font-medium max-w-[150px] truncate">
                          {member.display_name || member.username}
                        </span>
                        
                        {/* Game indicator */}
                        {member.game && (
                          <Gamepad2 className="w-3 h-3 text-purple-400" />
                        )}
                        
                        {/* Action buttons on hover - only show if logged in */}
                        {user ? (
                          <div className="hidden group-hover:flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openChat(member);
                              }}
                              className="p-1 hover:bg-blue-600/30 rounded"
                              title="Send Message"
                            >
                              <MessageSquare className="w-3 h-3 text-blue-400" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toast.success(`Challenge invite sent to ${member.display_name}!`);
                              }}
                              className="p-1 hover:bg-purple-600/30 rounded"
                              title="Invite to Challenge"
                          >
                            <Swords className="w-3 h-3 text-purple-400" />
                          </button>
                        </div>
                      ) : (
                        <div className="hidden group-hover:flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.error('Please sign in to send messages');
                            }}
                            className="p-1 hover:bg-gray-600/30 rounded cursor-not-allowed opacity-50"
                            title="Sign in to message"
                          >
                            <MessageSquare className="w-3 h-3 text-gray-500" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.error('Please sign in to send challenges');
                            }}
                            className="p-1 hover:bg-gray-600/30 rounded cursor-not-allowed opacity-50"
                            title="Sign in to challenge"
                          >
                            <Swords className="w-3 h-3 text-gray-500" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <span className="text-gray-400 dark:text-gray-500 text-sm px-4">No players online</span>
              )}
            </div>
          </div>
          ) : (
            /* Not logged in - show message to login to see members */
            <div className="flex-1 flex items-center justify-center">
              <div className="flex items-center gap-2 px-4 py-2 text-gray-400 text-sm">
                <Users className="w-4 h-4" />
                <span>Sign in to see who's online</span>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex items-center gap-2 pl-4 border-l border-gray-700 dark:border-gray-700 relative">
            {/* Messages/Inbox Button - Always visible for logged-in users */}
            {user && (
              <button
                onClick={openInbox}
                data-inbox-button="true"
                className={`relative p-2 rounded-lg transition-colors ${
                  showInbox ? 'bg-cyan-600/30 text-cyan-400' : 'hover:bg-slate-700 dark:hover:bg-slate-700 text-gray-400 dark:text-gray-400 hover:text-white dark:hover:text-white'
                } ${unreadCount > 0 ? 'animate-pulse' : ''}`}
                title="Inbox"
              >
                <Mail className={`w-5 h-5 ${unreadCount > 0 ? 'text-yellow-400' : ''}`} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            
            {/* Inbox Dropdown with click-outside-to-close */}
            {showInbox && (
              <>
                {/* Invisible overlay to catch outside clicks */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowInbox(false)}
                />
                <div className="absolute bottom-full right-0 mb-2 w-80 bg-slate-900 dark:bg-slate-900 border border-slate-700 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden z-50">
                  <div className="bg-gradient-to-r from-[#5865F2] to-purple-600 px-4 py-3 flex items-center justify-between">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Messages
                      {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {unreadCount} new
                        </span>
                      )}
                    </h3>
                    <button 
                      onClick={() => setShowInbox(false)}
                      className="text-white/70 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {loadingInbox ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 text-purple-400 animate-spin" />
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageCircle className="w-10 h-10 text-gray-600 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400 dark:text-gray-400 text-sm">No messages yet</p>
                      <p className="text-gray-500 dark:text-gray-500 text-xs">Start a conversation with someone!</p>
                    </div>
                  ) : (
                    conversations.map((conv, index) => (
                      <button
                        key={conv.user_id || index}
                        onClick={() => openChatFromInbox(conv)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-800 transition-colors border-b border-slate-800 last:border-0"
                      >
                        {conv.avatar ? (
                          <img src={conv.avatar} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
                            {(conv.name || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0 text-left">
                          <div className="flex items-center justify-between">
                            <p className="text-white text-sm font-medium truncate">
                              {conv.name || conv.discord_username || 'Unknown'}
                            </p>
                            <span className="text-gray-500 text-xs">
                              {conv.last_message_time ? new Date(conv.last_message_time).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <p className="text-gray-400 text-xs truncate">
                            {conv.is_from_me ? 'You: ' : ''}{conv.last_message || 'No messages'}
                          </p>
                        </div>
                        {conv.unread_count > 0 && (
                          <span className="bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
              </>
            )}
            
            <Button
              size="sm"
              variant="ghost"
              onClick={fetchOnlineMembers}
              className="text-gray-400 hover:text-white hover:bg-slate-700"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              size="sm"
              onClick={joinDiscordServer}
              className="bg-[#5865F2] hover:bg-[#4752C4] text-white"
            >
              <MessageCircle className="w-4 h-4 mr-1" />
              Open Discord
            </Button>
          </div>
        </div>
      </div>
      
      {/* Chat Modal */}
      <ChatModal
        isOpen={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setChatRecipient(null);
          fetchUnreadCount();
        }}
        recipient={chatRecipient}
        currentUserId={user?.user_id}
      />
    </div>
  );
};

export default DiscordBottomBar;
